import React, { useCallback, useContext } from 'react';
import { IUserDownloadAddonAbstract } from '../../resources/models/IUserDownloads';
import { DownloadsProductAddons } from './DownloadProductAddons';
import { DownloadProductAddonsTable } from './DownloadProductAddonsTable';
import { createFetcher, useAutoFetch } from '../../contexts/Fetcher';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../PageHeader/PageHeader';
import { useIntParams } from '../../hooks/useIntParams';
import { useTranslation } from 'react-i18next';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { UserDownloadsFetcher } from '../../fetchers/UserDownloadsFetcher';
import { apiUserDownloads } from '../../resources/api/apiUserDownloads';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { IUserExample } from '../../resources/models/IUserExample';
import { IUserEngineContent } from '../../resources/models/IUserEngineContent';

const getContentMeta = (content: IUserDownloadAddonAbstract) => (content as IUserEngineContent).content;
const getExampleMeta = (content: IUserDownloadAddonAbstract) => (content as IUserExample).example;

interface IUrlParams {
  product: string;
  version: string;
}

const { useGetEngineContent, useGetExamples, useGetTools, useGetLens } = apiUserDownloads;

const EngineContentFetcher = createFetcher<IUserDownloadAddonAbstract[]>(() => {
  const { version } = useParams<IUrlParams>();
  const { product } = useIntParams<IUrlParams>();
  const { organization } = useContext(OrganizationContext);
  const { execute } = useGetEngineContent(organization ?? void 0);
  return useCallback(async () => (await execute(product, version)).userEngineContent, [execute]);
});
const ExamplesFetcher = createFetcher<IUserDownloadAddonAbstract[]>(() => {
  const { version } = useParams<IUrlParams>();
  const { product } = useIntParams<IUrlParams>();
  const { organization } = useContext(OrganizationContext);
  const { execute } = useGetExamples(organization ?? void 0);
  return useCallback(async () => (await execute(product, version)).userExamples, [execute]);
});
const ToolsFetcher = createFetcher(() => {
  const { version } = useParams<IUrlParams>();
  const { product } = useIntParams<IUrlParams>();
  const { organization } = useContext(OrganizationContext);
  const { execute } = useGetTools(organization ?? void 0);
  return useCallback(async () => await execute(product, version), [execute]);
});
const LensFetcher = createFetcher(() => {
  const { product } = useIntParams<IUrlParams>();
  const { organization } = useContext(OrganizationContext);
  const { execute } = useGetLens(organization ?? void 0);
  return useCallback(async () => await execute(product), [execute]);
});

const getDownloadsProductAddonsPage = (addonType: 'engineContent' | 'demoProjects' | 'tools' | 'lens') => {
  const DownloadsProductAddonsPage = () => {
    const { data: downloads } = useContext(UserDownloadsFetcher.Context);
    useAutoFetch(UserDownloadsFetcher);

    const { product } = useIntParams<IUrlParams>();
    const { version } = useParams<IUrlParams>();
    const { t } = useTranslation();

    const pageTitle =
      addonType === 'engineContent'
        ? t('downloads.engineContentTitle')
        : addonType === 'demoProjects'
        ? t('downloads.examplesTitle')
        : addonType === 'tools'
        ? t('downloads.toolsTitle')
        : addonType === 'lens'
        ? t('downloads.lensTitle')
        : addonType;

    const productName = downloads?.userDownloads?.find((item) => item.product.id === product)?.product?.name;

    return (
      <div>
        <PageHeader
          title={pageTitle}
          crumbs={[
            {
              url: AuthenticatedRoutes.UserDownloads(),
              title: t('downloads.title'),
            },
            version
              ? {
                  url: AuthenticatedRoutes.UserDownloadsProductVersion(product, version),
                  title: t('downloads.versionBreadcrumb', { version, productName }),
                }
              : {
                  url: AuthenticatedRoutes.UserDownloadsProduct(product),
                  title: productName ?? '',
                },
          ]}
        />

        {addonType === 'engineContent' ? (
          <DownloadsProductAddons getMeta={getContentMeta} fetcher={EngineContentFetcher} />
        ) : null}
        {addonType === 'demoProjects' ? (
          <DownloadsProductAddons getMeta={getExampleMeta} fetcher={ExamplesFetcher} />
        ) : null}
        {addonType === 'tools' ? <DownloadProductAddonsTable fetcher={ToolsFetcher} /> : null}
        {addonType === 'lens' ? <DownloadProductAddonsTable fetcher={LensFetcher} /> : null}
      </div>
    );
  };
  return DownloadsProductAddonsPage;
};

export const DownloadsProductContentPage = getDownloadsProductAddonsPage('engineContent');
export const DownloadsProductExamplesPage = getDownloadsProductAddonsPage('demoProjects');
export const DownloadsProductToolsPage = getDownloadsProductAddonsPage('tools');
export const DownloadsProductLensPage = getDownloadsProductAddonsPage('lens');
